// Hero3.js - Contains functions for creating an advanced Hero section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a complete Hero section with nested elements
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the hero is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The hero elements
 */
export const createHeroElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const heroSectionClass = 'hero3-section';
  const heroBgShapeClass = 'hero3-bg-shape';
  const heroContainerClass = 'hero3-container';
  const heroLeftColClass = 'hero3-left-col';
  const heroRightColClass = 'hero3-right-col';
  const heroTitleClass = 'hero3-title';
  const heroGradientTextClass = 'hero3-gradient-text';
  const heroSubtitleClass = 'hero3-subtitle';
  const heroFeaturesClass = 'hero3-features';
  const heroFeatureItemClass = 'hero3-feature-item';
  const heroFeatureIconClass = 'hero3-feature-icon';
  const heroButtonsClass = 'hero3-buttons';
  const heroPrimaryBtnClass = 'hero3-primary-btn';
  const heroSecondaryBtnClass = 'hero3-secondary-btn';
  const heroVideoWrapperClass = 'hero3-video-wrapper';
  const heroVideoCoverClass = 'hero3-video-cover';
  const heroPlayBtnClass = 'hero3-play-btn';
  const heroTrustBadgesClass = 'hero3-trust-badges';
  const heroTrustTextClass = 'hero3-trust-text';
  const heroLogosWrapperClass = 'hero3-logos-wrapper';
  const heroLogoClass = 'hero3-logo';
  const heroFloatingElemClass = 'hero3-floating-element';
  const heroDecorationClass = 'hero3-decoration';
  
  // Desktop Styles (default)
  const heroSectionStyles = {
    position: 'relative',
    paddingTop: '80px',
    paddingBottom: '100px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f6f2ff',
    background: 'linear-gradient(135deg, #f6f2ff 0%, #f0f9ff 100%)',
    color: '#1a1a2e',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden'
  };

  const heroBgShapeStyles = {
    position: 'absolute',
    top: '0',
    right: '0',
    width: '70%',
    height: '90%',
    background: 'radial-gradient(circle at 70% 40%, rgba(124, 58, 237, 0.08) 0%, rgba(124, 58, 237, 0) 70%)',
    borderRadius: '30% 70% 70% 30% / 30% 40% 60% 70%',
    zIndex: '0',
    transform: 'translateY(-10%)'
  };

  const heroContainerStyles = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1280px',
    width: '100%',
    margin: '0 auto',
    gap: '40px',
    position: 'relative',
    zIndex: '1'
  };

  const heroLeftColStyles = {
    flex: '1.2',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    maxWidth: '640px',
    position: 'relative',
    zIndex: '2'
  };

  const heroRightColStyles = {
    flex: '1',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    minHeight: '480px'
  };

  const heroTitleStyles = {
    margin: '0',
    fontSize: '52px',
    fontWeight: '800',
    color: '#1a1a2e',
    lineHeight: '1.1',
    letterSpacing: '-0.02em'
  };

  const heroGradientTextStyles = {
    background: 'linear-gradient(90deg, #7c3aed 0%, #4f46e5 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontWeight: '800'
  };

  const heroSubtitleStyles = {
    margin: '0',
    fontSize: '18px',
    fontWeight: '400',
    color: '#4b5563',
    lineHeight: '1.6',
    maxWidth: '560px'
  };

  const heroFeaturesStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    marginTop: '8px'
  };

  const heroFeatureItemStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '16px',
    color: '#4b5563'
  };

  const heroFeatureIconStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#7c3aed',
    color: '#ffffff'
  };

  const heroButtonsStyles = {
    display: 'flex',
    gap: '16px',
    marginTop: '32px'
  };

  const heroPrimaryBtnStyles = {
    padding: '16px 32px',
    backgroundColor: '#7c3aed',
    color: '#ffffff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 10px 25px -5px rgba(124, 58, 237, 0.3)'
  };

  const heroSecondaryBtnStyles = {
    padding: '16px 32px',
    backgroundColor: 'transparent',
    color: '#7c3aed',
    border: '1px solid #7c3aed',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  };

  const heroVideoWrapperStyles = {
    position: 'relative',
    width: '100%',
    maxWidth: '560px',
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
    transform: 'perspective(1000px) rotateY(5deg) rotateX(5deg)',
    transition: 'transform 0.5s ease',
    aspectRatio: '16/9'
  };

  const heroVideoCoverStyles = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '20px'
  };

  const heroPlayBtnStyles = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease'
  };

  const heroPlayIconStyles = {
    fontSize: '24px',
    color: '#7c3aed',
    marginLeft: '4px' // Small offset to visually center the triangle
  };

  const heroTrustBadgesStyles = {
    marginTop: '48px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  };

  const heroTrustTextStyles = {
    fontSize: '14px',
    color: '#6b7280',
    textAlign: 'center'
  };

  const heroLogosWrapperStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '30px',
    flexWrap: 'wrap'
  };

  const heroLogoStyles = {
    height: '28px',
    filter: 'grayscale(100%)',
    opacity: '0.7',
    transition: 'all 0.3s ease'
  };

  const heroFloatingElemStyles = {
    position: 'absolute',
    borderRadius: '20px',
    backgroundColor: '#ffffff',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.05)',
    padding: '16px',
    zIndex: '3',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    animation: 'float 5s ease-in-out infinite'
  };

  const heroFloatingNumberStyles = {
    fontWeight: '700',
    color: '#7c3aed'
  };

  const heroDecorationStyles = {
    position: 'absolute',
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    background: 'linear-gradient(45deg, #7c3aed 0%, #4f46e5 100%)',
    filter: 'blur(30px)',
    opacity: '0.3',
    zIndex: '1'
  };

  // Tablet Styles
  const tabletHeroContainerStyles = {
    ...heroContainerStyles,
    gap: '30px',
    padding: '0 20px'
  };

  const tabletHeroTitleStyles = {
    ...heroTitleStyles,
    fontSize: '42px'
  };

  const tabletHeroVideoWrapperStyles = {
    ...heroVideoWrapperStyles,
    transform: 'perspective(1000px) rotateY(5deg) rotateX(3deg) scale(0.9)'
  };

  // Mobile Styles
  const mobileHeroContainerStyles = {
    ...heroContainerStyles,
    flexDirection: 'column',
    padding: '0 15px',
    gap: '50px'
  };

  const mobileHeroLeftColStyles = {
    ...heroLeftColStyles,
    alignItems: 'center',
    textAlign: 'center'
  };

  const mobileHeroTitleStyles = {
    ...heroTitleStyles,
    fontSize: '36px'
  };

  const mobileHeroSubtitleStyles = {
    ...heroSubtitleStyles,
    fontSize: '16px'
  };

  const mobileHeroButtonsStyles = {
    ...heroButtonsStyles,
    flexDirection: 'column',
    width: '100%'
  };

  const mobileHeroVideoWrapperStyles = {
    ...heroVideoWrapperStyles,
    transform: 'none',
    maxWidth: '100%'
  };

  const mobileHeroFeaturesStyles = {
    ...heroFeaturesStyles,
    alignItems: 'center'
  };

  const mobileHeroFeatureItemStyles = {
    ...heroFeatureItemStyles,
    justifyContent: 'center'
  };

  const mobileHeroLogosWrapperStyles = {
    ...heroLogosWrapperStyles,
    justifyContent: 'center'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(heroSectionClass, heroSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(heroBgShapeClass, heroBgShapeStyles);
    newRegistry[DEVICES.DESKTOP].set(heroContainerClass, heroContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(heroLeftColClass, heroLeftColStyles);
    newRegistry[DEVICES.DESKTOP].set(heroRightColClass, heroRightColStyles);
    newRegistry[DEVICES.DESKTOP].set(heroTitleClass, heroTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(heroGradientTextClass, heroGradientTextStyles);
    newRegistry[DEVICES.DESKTOP].set(heroSubtitleClass, heroSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(heroFeaturesClass, heroFeaturesStyles);
    newRegistry[DEVICES.DESKTOP].set(heroFeatureItemClass, heroFeatureItemStyles);
    newRegistry[DEVICES.DESKTOP].set(heroFeatureIconClass, heroFeatureIconStyles);
    newRegistry[DEVICES.DESKTOP].set(heroButtonsClass, heroButtonsStyles);
    newRegistry[DEVICES.DESKTOP].set(heroPrimaryBtnClass, heroPrimaryBtnStyles);
    newRegistry[DEVICES.DESKTOP].set(heroSecondaryBtnClass, heroSecondaryBtnStyles);
    newRegistry[DEVICES.DESKTOP].set(heroVideoWrapperClass, heroVideoWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(heroVideoCoverClass, heroVideoCoverStyles);
    newRegistry[DEVICES.DESKTOP].set(heroPlayBtnClass, heroPlayBtnStyles);
    newRegistry[DEVICES.DESKTOP].set(heroTrustBadgesClass, heroTrustBadgesStyles);
    newRegistry[DEVICES.DESKTOP].set(heroTrustTextClass, heroTrustTextStyles);
    newRegistry[DEVICES.DESKTOP].set(heroLogosWrapperClass, heroLogosWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(heroLogoClass, heroLogoStyles);
    newRegistry[DEVICES.DESKTOP].set(heroFloatingElemClass, heroFloatingElemStyles);
    newRegistry[DEVICES.DESKTOP].set(heroDecorationClass, heroDecorationStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(heroContainerClass, tabletHeroContainerStyles);
    newRegistry[DEVICES.TABLET].set(heroTitleClass, tabletHeroTitleStyles);
    newRegistry[DEVICES.TABLET].set(heroVideoWrapperClass, tabletHeroVideoWrapperStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(heroContainerClass, mobileHeroContainerStyles);
    newRegistry[DEVICES.MOBILE].set(heroLeftColClass, mobileHeroLeftColStyles);
    newRegistry[DEVICES.MOBILE].set(heroTitleClass, mobileHeroTitleStyles);
    newRegistry[DEVICES.MOBILE].set(heroSubtitleClass, mobileHeroSubtitleStyles);
    newRegistry[DEVICES.MOBILE].set(heroButtonsClass, mobileHeroButtonsStyles);
    newRegistry[DEVICES.MOBILE].set(heroVideoWrapperClass, mobileHeroVideoWrapperStyles);
    newRegistry[DEVICES.MOBILE].set(heroFeaturesClass, mobileHeroFeaturesStyles);
    newRegistry[DEVICES.MOBILE].set(heroFeatureItemClass, mobileHeroFeatureItemStyles);
    newRegistry[DEVICES.MOBILE].set(heroLogosWrapperClass, mobileHeroLogosWrapperStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    heroSectionClass,
    heroBgShapeClass,
    heroContainerClass,
    heroLeftColClass,
    heroRightColClass,
    heroTitleClass,
    heroGradientTextClass,
    heroSubtitleClass,
    heroFeaturesClass,
    heroFeatureItemClass,
    heroFeatureIconClass,
    heroButtonsClass,
    heroPrimaryBtnClass,
    heroSecondaryBtnClass,
    heroVideoWrapperClass,
    heroVideoCoverClass,
    heroPlayBtnClass,
    heroTrustBadgesClass,
    heroTrustTextClass,
    heroLogosWrapperClass,
    heroLogoClass,
    heroFloatingElemClass,
    heroDecorationClass
  ]));

  // Generate IDs for all elements
  const heroSectionId = Date.now();
  const bgShapeID = Date.now() + 1;
  const containerID = Date.now() + 2;
  const leftColID = Date.now() + 3;
  const rightColID = Date.now() + 4;
  const titleID = Date.now() + 5;
  const gradientTextID = Date.now() + 6;
  const subtitleID = Date.now() + 7;
  const featuresID = Date.now() + 8;
  const feature1ID = Date.now() + 9;
  const feature1IconID = Date.now() + 10;
  const feature2ID = Date.now() + 11;
  const feature2IconID = Date.now() + 12;
  const feature3ID = Date.now() + 13;
  const feature3IconID = Date.now() + 14;
  const buttonsWrapperID = Date.now() + 15;
  const primaryButtonID = Date.now() + 16;
  const secondaryButtonID = Date.now() + 17;
  const videoWrapperID = Date.now() + 18;
  const videoCoverID = Date.now() + 19;
  const playBtnID = Date.now() + 20;
  const playIconID = Date.now() + 21;
  const decoration1ID = Date.now() + 22;
  const decoration2ID = Date.now() + 23;
  const floatingElemID = Date.now() + 24;
  const floatingNumberID = Date.now() + 25;
  const trustBadgesID = Date.now() + 26;
  const trustTextID = Date.now() + 27;
  const logosWrapperID = Date.now() + 28;
  const logo1ID = Date.now() + 29;
  const logo2ID = Date.now() + 30;
  const logo3ID = Date.now() + 31;
  const logo4ID = Date.now() + 32;

  const newElements = [
    // Main Hero Section Container
    {
      id: heroSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Hero Section 3',
      parentId: null,
      className: heroSectionClass,
      styles: {},
      children: [bgShapeID, containerID, decoration1ID, decoration2ID, floatingElemID],
      text: '',
      attributes: {
        'data-element': 'hero-section',
        'data-component': 'section',
        'data-section-type': 'hero',
        'data-page-id': currentPageId
      }
    },
    
    // Background Shape
    {
      id: bgShapeID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Background Shape',
      parentId: heroSectionId,
      hierarchyName: "Background Shape",
      className: heroBgShapeClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'hero-bg-shape'
      }
    },
    
    // Decoration 1
    {
      id: decoration1ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Decoration 1',
      parentId: heroSectionId,
      hierarchyName: "Decoration 1",
      className: heroDecorationClass,
      styles: {
        top: '15%',
        left: '10%'
      },
      children: [],
      text: '',
      attributes: {
        'data-element': 'hero-decoration'
      }
    },
    
    // Decoration 2
    {
      id: decoration2ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Decoration 2',
      parentId: heroSectionId,
      hierarchyName: "Decoration 2",
      className: heroDecorationClass,
      styles: {
        bottom: '20%',
        right: '15%'
      },
      children: [],
      text: '',
      attributes: {
        'data-element': 'hero-decoration'
      }
    },
    
    // Floating Element (like a user stats card)
    {
      id: floatingElemID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Floating Element',
      parentId: heroSectionId,
      hierarchyName: "Floating Element",
      className: heroFloatingElemClass,
      styles: {
        bottom: '25%',
        right: '5%'
      },
      children: [floatingNumberID],
      text: 'websites built this month',
      attributes: {
        'data-element': 'hero-floating-element'
      }
    },
    
    // Floating Number
    {
      id: floatingNumberID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Floating Number',
      parentId: floatingElemID,
      hierarchyName: "Floating Number",
      styles: heroFloatingNumberStyles,
      children: [],
      text: '5,000+',
      attributes: {
        'data-element': 'hero-floating-number'
      }
    },
    
    // Container for columns
    {
      id: containerID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hero Container',
      parentId: heroSectionId,
      hierarchyName: "Hero Container",
      className: heroContainerClass,
      styles: {},
      children: [leftColID, rightColID],
      text: '',
      attributes: {
        'data-element': 'hero-container'
      }
    },
    
    // Left Column
    {
      id: leftColID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Left Column',
      parentId: containerID,
      hierarchyName: "Left Column",
      className: heroLeftColClass,
      styles: {},
      children: [titleID, subtitleID, featuresID, buttonsWrapperID, trustBadgesID],
      text: '',
      attributes: {
        'data-element': 'hero-left-col'
      }
    },
    
    // Right Column
    {
      id: rightColID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Right Column',
      parentId: containerID,
      hierarchyName: "Right Column",
      className: heroRightColClass,
      styles: {},
      children: [videoWrapperID],
      text: '',
      attributes: {
        'data-element': 'hero-right-col'
      }
    },
    
    // Title
    {
      id: titleID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h1',
      name: 'Hero Title',
      parentId: leftColID,
      hierarchyName: "Hero Title",
      className: heroTitleClass,
      styles: {},
      children: [gradientTextID],
      text: 'Build beautiful websites with ',
      attributes: {
        'data-element': 'hero-title'
      },
      interactions: {
        events: [{
          id: Date.now(),
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'slide-up',
            duration: '0.8s',
            delay: '0.1s',
            iterations: '1'
          }
        }]
      }
    },
    
    // Gradient Text Span
    {
      id: gradientTextID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Gradient Text',
      parentId: titleID,
      hierarchyName: "Gradient Text",
      className: heroGradientTextClass,
      styles: {},
      children: [],
      text: 'drag & drop simplicity',
      attributes: {
        'data-element': 'hero-gradient-text'
      }
    },
    
    // Subtitle
    {
      id: subtitleID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Hero Subtitle',
      parentId: leftColID,
      hierarchyName: "Hero Subtitle",
      className: heroSubtitleClass,
      styles: {},
      children: [],
      text: 'Launch your site in minutes, not months. Our intuitive builder combines professional design with easy customization—no coding required.',
      attributes: {
        'data-element': 'hero-subtitle'
      },
      interactions: {
        events: [{
          id: Date.now() + 1,
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'slide-up',
            duration: '0.8s',
            delay: '0.2s',
            iterations: '1'
          }
        }]
      }
    },
    
    // Features List
    {
      id: featuresID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Features List',
      parentId: leftColID,
      hierarchyName: "Features List",
      className: heroFeaturesClass,
      styles: {},
      children: [feature1ID, feature2ID, feature3ID],
      text: '',
      attributes: {
        'data-element': 'hero-features'
      }
    },
    
    // Feature 1
    {
      id: feature1ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Feature 1',
      parentId: featuresID,
      hierarchyName: "Feature 1",
      className: heroFeatureItemClass,
      styles: {},
      children: [feature1IconID],
      text: '250+ pre-designed sections',
      attributes: {
        'data-element': 'hero-feature-item'
      }
    },
    
    // Feature 1 Icon
    {
      id: feature1IconID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Feature 1 Icon',
      parentId: feature1ID,
      hierarchyName: "Feature 1 Icon",
      className: heroFeatureIconClass,
      styles: {
        marginRight: '12px'
      },
      children: [],
      text: '✓',
      attributes: {
        'data-element': 'hero-feature-icon'
      }
    },
    
    // Feature 2
    {
      id: feature2ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Feature 2',
      parentId: featuresID,
      hierarchyName: "Feature 2",
      className: heroFeatureItemClass,
      styles: {},
      children: [feature2IconID],
      text: 'Fully responsive on all devices',
      attributes: {
        'data-element': 'hero-feature-item'
      }
    },
    
    // Feature 2 Icon
    {
      id: feature2IconID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Feature 2 Icon',
      parentId: feature2ID,
      hierarchyName: "Feature 2 Icon",
      className: heroFeatureIconClass,
      styles: {
        marginRight: '12px'
      },
      children: [],
      text: '✓',
      attributes: {
        'data-element': 'hero-feature-icon'
      }
    },
    
    // Feature 3
    {
      id: feature3ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Feature 3',
      parentId: featuresID,
      hierarchyName: "Feature 3",
      className: heroFeatureItemClass,
      styles: {},
      children: [feature3IconID],
      text: 'No coding skills required',
      attributes: {
        'data-element': 'hero-feature-item'
      }
    },
    
    // Feature 3 Icon
    {
      id: feature3IconID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Feature 3 Icon',
      parentId: feature3ID,
      hierarchyName: "Feature 3 Icon",
      className: heroFeatureIconClass,
      styles: {
        marginRight: '12px'
      },
      children: [],
      text: '✓',
      attributes: {
        'data-element': 'hero-feature-icon'
      }
    },
    
    // Buttons Wrapper
    {
      id: buttonsWrapperID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hero Buttons',
      parentId: leftColID,
      hierarchyName: "Hero Buttons",
      className: heroButtonsClass,
      styles: {},
      children: [primaryButtonID, secondaryButtonID],
      text: '',
      attributes: {
        'data-element': 'hero-buttons'
      }
    },
    
    // Primary Button
    {
      id: primaryButtonID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Primary Button',
      parentId: buttonsWrapperID,
      hierarchyName: "Primary Button",
      className: heroPrimaryBtnClass,
      styles: {},
      children: [],
      text: 'Try It Free',
      attributes: {
        'data-element': 'primary-button'
      },
      stateClassName: {
        hover: '',
        pressed: '',
        focused: ''
      },
      currentState: 'default',
      interactions: {
        events: [{
          id: Date.now() + 2,
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'slide-up',
            duration: '0.8s',
            delay: '0.3s',
            iterations: '1'
          }
        }]
      }
    },
    
    // Secondary Button
    {
      id: secondaryButtonID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Secondary Button',
      parentId: buttonsWrapperID,
      hierarchyName: "Secondary Button",
      className: heroSecondaryBtnClass,
      styles: {},
      children: [],
      text: 'View Examples',
      attributes: {
        'data-element': 'secondary-button'
      },
      stateClassName: {
        hover: '',
        pressed: '',
        focused: ''
      },
      currentState: 'default',
      interactions: {
        events: [{
          id: Date.now() + 3,
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'slide-up',
            duration: '0.8s',
            delay: '0.4s',
            iterations: '1'
          }
        }]
      }
    },
    
    // Trust Badges Section
    {
      id: trustBadgesID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Trust Badges',
      parentId: leftColID,
      hierarchyName: "Trust Badges",
      className: heroTrustBadgesClass,
      styles: {},
      children: [trustTextID, logosWrapperID],
      text: '',
      attributes: {
        'data-element': 'hero-trust-badges'
      }
    },
    
    // Trust Text
    {
      id: trustTextID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Trust Text',
      parentId: trustBadgesID,
      hierarchyName: "Trust Text",
      className: heroTrustTextClass,
      styles: {},
      children: [],
      text: 'TRUSTED BY INNOVATIVE COMPANIES',
      attributes: {
        'data-element': 'hero-trust-text'
      }
    },
    
    // Logos Wrapper
    {
      id: logosWrapperID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Logos Wrapper',
      parentId: trustBadgesID,
      hierarchyName: "Logos Wrapper",
      className: heroLogosWrapperClass,
      styles: {},
      children: [logo1ID, logo2ID, logo3ID, logo4ID],
      text: '',
      attributes: {
        'data-element': 'hero-logos-wrapper'
      }
    },
    
    // Logo 1
    {
      id: logo1ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Logo 1',
      parentId: logosWrapperID,
      hierarchyName: "Logo 1",
      className: heroLogoClass,
      styles: {},
      children: [],
      text: '',
      src: 'https://cdn-icons-png.flaticon.com/512/5968/5968866.png',
      attributes: {
        'data-element': 'hero-logo',
        'alt': 'Company logo 1'
      }
    },
    
    // Logo 2
    {
      id: logo2ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Logo 2',
      parentId: logosWrapperID,
      hierarchyName: "Logo 2",
      className: heroLogoClass,
      styles: {},
      children: [],
      text: '',
      src: 'https://cdn-icons-png.flaticon.com/512/5968/5968919.png',
      attributes: {
        'data-element': 'hero-logo',
        'alt': 'Company logo 2'
      }
    },
    
    // Logo 3
    {
      id: logo3ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Logo 3',
      parentId: logosWrapperID,
      hierarchyName: "Logo 3",
      className: heroLogoClass,
      styles: {},
      children: [],
      text: '',
      src: 'https://cdn-icons-png.flaticon.com/512/5968/5968863.png',
      attributes: {
        'data-element': 'hero-logo',
        'alt': 'Company logo 3'
      }
    },
    
    // Logo 4
    {
      id: logo4ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Logo 4',
      parentId: logosWrapperID,
      hierarchyName: "Logo 4",
      className: heroLogoClass,
      styles: {},
      children: [],
      text: '',
      src: 'https://cdn-icons-png.flaticon.com/512/5968/5968853.png',
      attributes: {
        'data-element': 'hero-logo',
        'alt': 'Company logo 4'
      }
    },
    
    // Video Wrapper
    {
      id: videoWrapperID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Video Wrapper',
      parentId: rightColID,
      hierarchyName: "Video Wrapper",
      className: heroVideoWrapperClass,
      styles: {},
      children: [videoCoverID, playBtnID],
      text: '',
      attributes: {
        'data-element': 'hero-video-wrapper'
      }
    },
    
    // Video Cover
    {
      id: videoCoverID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Video Cover',
      parentId: videoWrapperID,
      hierarchyName: "Video Cover",
      className: heroVideoCoverClass,
      styles: {},
      children: [],
      text: '',
      src: 'https://images.unsplash.com/photo-1593720213428-28a5b9e94613?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1770&q=80',
      attributes: {
        'data-element': 'hero-video-cover',
        'alt': 'Video thumbnail'
      }
    },
    
    // Play Button
    {
      id: playBtnID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Play Button',
      parentId: videoWrapperID,
      hierarchyName: "Play Button",
      className: heroPlayBtnClass,
      styles: {},
      children: [playIconID],
      text: '',
      attributes: {
        'data-element': 'hero-play-btn'
      },
      interactions: {
        events: [{
          id: Date.now() + 4,
          type: 'animation',
          action: 'animate',
          trigger: 'onHover',
          value: {
            animation: 'pulse',
            duration: '1s',
            delay: '0s',
            iterations: 'infinite'
          }
        }]
      }
    },
    
    // Play Icon
    {
      id: playIconID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Play Icon',
      parentId: playBtnID,
      hierarchyName: "Play Icon",
      styles: heroPlayIconStyles,
      children: [],
      text: '▶',
      attributes: {
        'data-element': 'hero-play-icon'
      }
    }
  ];
  
  // Return all elements
  return {
    heroSection: newElements[0],
    elements: newElements
  };
};

export default createHeroElements; 